/**
 * Simple Error Handler Service
 */

import {
  ERROR_CODES,
  ERROR_CONFIG,
  HTTP_STATUS_MAPPING,
  NETWORK_ERROR_MAPPING
} from "@/config/errorCodes";

export default class ErrorHandler {
  constructor(store, router) {
    this.store = store;
    this.router = router;
  }

  /**
   * Handle an error
   */
  handleError(error, context = {}) {
    const processedError = this.processError(error, context);
    this.displayError(processedError);
    return processedError;
  }

  /**
   * Process error and determine error code
   */
  processError(error, context = {}) {
    let errorCode = ERROR_CODES.UNKNOWN_ERROR;
    let errorMessage = "An unexpected error occurred";

    // Handle HTTP errors
    if (error.response) {
      const status = error.response.status;
      errorCode = HTTP_STATUS_MAPPING[status] || ERROR_CODES.UNKNOWN_ERROR;
      errorMessage =
        error.response.data?.message || error.message || "HTTP Error";
    }
    // Handle network errors
    else if (error.request) {
      errorCode = this.getNetworkErrorCode(error);
      errorMessage = ERROR_CONFIG[errorCode].message;
    }
    // Handle custom errors
    else if (error.code && ERROR_CONFIG[error.code]) {
      errorCode = error.code;
      errorMessage = error.message || ERROR_CONFIG[error.code].message;
    }
    // Handle JavaScript errors
    else if (error instanceof Error) {
      errorCode = ERROR_CODES.UNKNOWN_ERROR;
      errorMessage = error.message || "A JavaScript error occurred";
    }

    const errorConfig =
      ERROR_CONFIG[errorCode] || ERROR_CONFIG[ERROR_CODES.UNKNOWN_ERROR];

    return {
      code: errorCode,
      message: errorMessage,
      color: errorConfig.color,
      config: errorConfig,
      context: context
    };
  }

  /**
   * Get error code for network errors
   */
  getNetworkErrorCode(error) {
    const message = error.message || "";

    for (const [key, code] of Object.entries(NETWORK_ERROR_MAPPING)) {
      if (message.includes(key)) {
        return code;
      }
    }

    return ERROR_CODES.NETWORK_ERROR;
  }

  /**
   * Display error to user
   */
  displayError(processedError) {
    const { message, color, config } = processedError;
    // Dispatch to store for UI display
    this.store.dispatch("httpResponse/setHttpResponse", {
      text: config?.message ?? message,
      color: color
    });

    // Handle auto-hide
    if (config.autoHide) {
      setTimeout(() => {
        this.store.dispatch("httpResponse/resetHttpResponse");
      }, config.duration);
    }

    // Handle redirects for auth errors
    if (processedError.code === ERROR_CODES.UNAUTHORIZED) {
      setTimeout(() => {
        this.router.push("/auth");
      }, 1000);
    }
  }
}
